import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { RevenueBreakdown } from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/RevenueBreakdownCard.types';
import {
  sortDataAscending,
  sortPositiveValuesAscending,
} from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/utils';
import { useEffect, useState } from 'react';
import { dynatraceTrackInstantAction } from 'utils/dynatrace';

const useRevenueBreakdown = (
  data: any,
  loading: boolean,
  marketFilter: string,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [zeroLinePosition, setZeroLinePosition] = useState(0);
  const [marketData, setMarketData] = useState<RevenueBreakdown[]>([]);
  const [sortedMarketData, setSortedMarketData] = useState<RevenueBreakdown[]>(
    [],
  );
  const [totalAbsoluteRevenue, setTotalAbsoluteRevenue] = useState(0);
  const [totalNegativeRevenue, setTotalNegativeRevenue] = useState(0);
  const [totalPositiveRevenue, setTotalPositiveRevenue] = useState(0);

  useEffect(() => {
    if (isOpen) {
      dynatraceTrackInstantAction(
        DYNATRACE_ACTIONS.VIEW_REVENUE_BREAKDOWN_EXPANDED,
      );
    }
  }, [isOpen]);

  useEffect(() => {
    if (data && !loading) {
      const tradeSummary = data.site.tradeSummary[marketFilter];
      const MARKET_DATA = Object.entries(tradeSummary.revenueBreakdown)
        .filter(([key, value]) => key !== '__typename' && value)
        .map(([key, value]) => ({
          market: key,
          revenue: value as number,
        }));
      setMarketData(MARKET_DATA);
      const SORTED_MARKET_DATA = sortPositiveValuesAscending(
        sortDataAscending(MARKET_DATA),
      );
      setSortedMarketData(SORTED_MARKET_DATA);

      const totalNegativeRevenue = MARKET_DATA.reduce(
        (acc, it) => (it.revenue < 0 ? acc + it.revenue : acc),
        0,
      );

      const totalPositiveRevenue = MARKET_DATA.reduce(
        (acc, it) => (it.revenue > 0 ? acc + it.revenue : acc),
        0,
      );
      const totalAbsoluteRevenue = totalPositiveRevenue + -totalNegativeRevenue;

      setTotalAbsoluteRevenue(totalAbsoluteRevenue);
      setTotalNegativeRevenue(totalNegativeRevenue);
      setTotalPositiveRevenue(totalPositiveRevenue);

      const zeroLineWidth: number =
        totalNegativeRevenue && totalAbsoluteRevenue
          ? (-totalNegativeRevenue / totalAbsoluteRevenue) * 100
          : 0;
      setZeroLinePosition(zeroLineWidth);
    }
  }, [data, loading, marketFilter]);

  const handleExpand = () => {
    setIsOpen(!isOpen);
  };

  const getRevenuePercentage = (market: { revenue: number }) => {
    // The percentage width of the bar
    return (
      Math.round((Math.abs(market.revenue) / totalAbsoluteRevenue) * 10000) /
      100
    );
  };

  const getPercentageOfGainOrLoss = (market: { revenue: number }) => {
    // The revenue percentage segregated by loss or gain

    if (market.revenue < 0) {
      return Math.round(
        Math.abs((market.revenue / totalNegativeRevenue) * 100),
      );
    }

    if (market.revenue > 0) {
      return Math.round(
        Math.abs((market.revenue / totalPositiveRevenue) * 100),
      );
    }

    return 0;
  };

  return {
    handleExpand,
    isOpen,
    zeroLinePosition,
    marketData,
    sortedMarketData,
    totalRevenue: totalAbsoluteRevenue,
    getRevenuePercentage,
    getPercentageOfGainOrLoss,
  };
};

export { useRevenueBreakdown };
